import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
    Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, Paper, Tab, Tabs, TextField, Typography,
} from "@mui/material";
import { BsCloudUpload } from "react-icons/bs";
import commonActions from "../../redux/common/actions";
import { createExportApi, getSchemaApi, listExportsApi } from "./api";
import SchemaBrowser from "./SchemaBrowser";
import HistoryList from "./HistoryList";
import ExportsList from "./ExportsList";

const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace";
const ACTIVE_STATUSES = ["queued", "running", "cancelling"];

// Only to colour an address that is obviously mistyped; the server checks properly.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** One pasted line can hold several addresses. */
const splitAddresses = (text) =>
    String(text).split(/[,;\s]+/).map((value) => value.trim()).filter(Boolean);

const QueryTool = () => {
    const dispatch = useDispatch();
    const editorRef = useRef(null);

    const [sql, setSql] = useState("");
    const [accessDenied, setAccessDenied] = useState(false);

    const [sideTab, setSideTab] = useState("tables");
    const [schema, setSchema] = useState({ tables: [], loading: true, error: "" });
    const [exports, setExports] = useState({ items: [], loading: true });

    // Export dialog: who the file's link and key should go to. `emails` is a
    // list, shown as chips.
    const [exportDialog, setExportDialog] = useState({ open: false, sql: "", name: "", emails: [] });
    const [emailInput, setEmailInput] = useState("");
    const [startingExport, setStartingExport] = useState(false);
    const exportPollRef = useRef(null);

    const showAlert = useCallback((status, message) => {
        dispatch({ type: commonActions.SET_ALERT, payload: { status, show: true, message } });
        setTimeout(() => {
            dispatch({ type: commonActions.SET_ALERT, payload: { status: null, show: false, message: null } });
        }, 3000);
    }, [dispatch]);

    const loadSchema = useCallback(async (refresh = false) => {
        setSchema((prev) => ({ ...prev, loading: true, error: "" }));
        try {
            const data = await getSchemaApi(refresh);
            if (data?.statusCode === 403) {
                setAccessDenied(true);
                return;
            }
            if (data?.statusCode === 200) {
                setSchema({ tables: data.result?.tables || [], loading: false, error: "" });
            } else {
                setSchema({ tables: [], loading: false, error: data?.message || "Unable to load schema" });
            }
        } catch (err) {
            setSchema({ tables: [], loading: false, error: "Unable to load schema" });
        }
    }, []);

    const loadExports = useCallback(async () => {
        setExports((prev) => ({ ...prev, loading: true }));
        try {
            const data = await listExportsApi();
            setExports({ items: data?.statusCode === 200 ? data.result || [] : [], loading: false });
        } catch (err) {
            setExports({ items: [], loading: false });
        }
    }, []);

    useEffect(() => {
        loadSchema();
        loadExports();
    }, [loadSchema, loadExports]);

    // Poll only while something is actually running, so an idle page is quiet.
    const hasActiveExport = exports.items.some((item) => ACTIVE_STATUSES.includes(item.status));

    useEffect(() => {
        clearInterval(exportPollRef.current);
        if (!hasActiveExport) return undefined;
        exportPollRef.current = setInterval(loadExports, 3000);
        return () => clearInterval(exportPollRef.current);
    }, [hasActiveExport, loadExports]);

    useEffect(() => () => clearInterval(exportPollRef.current), []);

    // Exports only the highlighted part of the editor when there is a selection.
    const getSqlToExport = () => {
        const el = editorRef.current;
        if (el && el.selectionEnd > el.selectionStart) {
            return sql.slice(el.selectionStart, el.selectionEnd);
        }
        return sql;
    };

    const openExportDialog = () => {
        const toExport = getSqlToExport();
        if (!toExport.trim()) return;
        // Keeps the name and addresses from last time: re-running a saved query
        // usually wants the same ones.
        setExportDialog((prev) => ({ ...prev, open: true, sql: toExport }));
    };

    const submitExport = async () => {
        // An address typed but not yet turned into a chip is still sitting in
        // the input. Clicking Start export means to send it there, so take it
        // along instead of mailing nobody.
        const pending = splitAddresses(emailInput);
        const recipients = [...new Set([...exportDialog.emails, ...pending])];
        if (pending.length) {
            setExportDialog((prev) => ({ ...prev, emails: recipients }));
            setEmailInput("");
        }

        const invalid = recipients.filter((email) => !EMAIL_RE.test(email));
        if (invalid.length) {
            showAlert("failed", `Check this address: ${invalid.join(", ")}`);
            return;
        }

        setStartingExport(true);
        try {
            const data = await createExportApi(exportDialog.sql, recipients, exportDialog.name.trim());
            if (data?.statusCode === 200) {
                setExportDialog((prev) => ({ ...prev, open: false }));
                setSideTab("exports");
                showAlert("success", data.message || "Export started");
                loadExports();
            } else if (data?.statusCode === 403) {
                setAccessDenied(true);
            } else {
                showAlert("failed", data?.message || "Could not start the export.");
            }
        } catch (err) {
            showAlert("failed", err?.response?.data?.message || "Could not start the export.");
        } finally {
            setStartingExport(false);
        }
    };

    /** Bring a past query back, with the name it was given. */
    const loadFromHistory = (item) => {
        setSql(item.querytext);
        setExportDialog((prev) => ({ ...prev, name: item.exportname || "" }));
        requestAnimationFrame(() => editorRef.current?.focus());
    };

    const insertAtCursor = (text) => {
        const el = editorRef.current;
        const start = el ? el.selectionStart : sql.length;
        const end = el ? el.selectionEnd : sql.length;
        const next = sql.slice(0, start) + text + sql.slice(end);
        setSql(next);
        requestAnimationFrame(() => {
            if (!el) return;
            el.focus();
            el.setSelectionRange(start + text.length, start + text.length);
        });
    };

    const handleEditorKeyDown = (e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
            e.preventDefault();
            openExportDialog();
        } else if (e.key === "Tab" && !e.shiftKey) {
            e.preventDefault();
            insertAtCursor("    ");
        }
    };

    if (accessDenied) {
        return (
            <Alert severity="warning" sx={{ mt: 2 }}>
                You need the <b>Query Tool</b> role to use this page. Ask an administrator to assign it,
                then log in again and select that role.
            </Alert>
        );
    }

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "320px minmax(0, 1fr)" },
                gap: 2,
                pb: 3,
            }}
        >
            <Paper
                variant="outlined"
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: { xs: 360, md: "calc(100vh - 190px)" },
                    minHeight: 360,
                    position: { md: "sticky" },
                    top: { md: 96 },
                }}
            >
                <Tabs
                    value={sideTab}
                    onChange={(e, value) => setSideTab(value)}
                    variant="fullWidth"
                    sx={{ borderBottom: "1px solid #e0e0e0", minHeight: 40, "& .MuiTab-root": { minHeight: 40, textTransform: "none" } }}
                >
                    <Tab value="tables" label="Tables" />
                    <Tab value="history" label="History" />
                    <Tab value="exports" label="Exports" />
                </Tabs>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    {sideTab === "tables" && (
                        <SchemaBrowser
                            tables={schema.tables}
                            loading={schema.loading}
                            error={schema.error}
                            onRefresh={() => loadSchema(true)}
                            onInsert={insertAtCursor}
                        />
                    )}
                    {/* Same rows as Exports, read as "queries I ran before": click
                        one to load it back into the editor and adjust it. */}
                    {sideTab === "history" && (
                        <HistoryList
                            items={exports.items}
                            loading={exports.loading}
                            onRefresh={loadExports}
                            onSelect={loadFromHistory}
                        />
                    )}
                    {sideTab === "exports" && (
                        <ExportsList
                            items={exports.items}
                            loading={exports.loading}
                            onRefresh={loadExports}
                            onChanged={loadExports}
                            onNotify={showAlert}
                        />
                    )}
                </Box>
            </Paper>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <TextField
                        inputRef={editorRef}
                        value={sql}
                        onChange={(e) => setSql(e.target.value)}
                        onKeyDown={handleEditorKeyDown}
                        placeholder={"SELECT candidateid, candidatename, emailid\nFROM cnd_candidatedetails\nWHERE ..."}
                        multiline
                        minRows={12}
                        maxRows={24}
                        fullWidth
                        spellCheck={false}
                        inputProps={{ style: { fontFamily: MONO, fontSize: 13, lineHeight: 1.6 }, "aria-label": "SQL query" }}
                    />

                    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5, mt: 1.5 }}>
                        <Button
                            variant="contained"
                            onClick={openExportDialog}
                            disabled={!sql.trim()}
                            startIcon={<BsCloudUpload />}
                            sx={{ textTransform: "none", minWidth: 150 }}
                        >
                            Export data
                        </Button>
                        <Button
                            onClick={() => { setSql(""); editorRef.current?.focus(); }}
                            disabled={!sql}
                            sx={{ textTransform: "none" }}
                        >
                            Clear
                        </Button>
                        <Typography sx={{ fontSize: 12, color: "text.secondary", flex: "1 1 240px" }}>
                            Read-only. Results are not shown here: the whole result is delivered as a
                            secure, encrypted file. Ctrl+Enter exports, or just the highlighted part.
                        </Typography>
                    </Box>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography sx={{ fontWeight: 500, mb: 1 }}>How this works</Typography>
                    <Typography component="ol" sx={{ m: 0, pl: 2.5, fontSize: 13, color: "text.secondary", lineHeight: 1.9 }}>
                        <li>Write a SELECT and choose <b>Export data</b>, adding the email addresses that should receive it.</li>
                        <li>The file is built in the background — watch it in the <b>Exports</b> tab, and stop it there if needed.</li>
                        <li>When it finishes, each recipient gets an email with the download link and the secret key.</li>
                        <li>Open the downloaded file in the Candidate Dump Decryptor with that key to get a CSV.</li>
                    </Typography>
                </Paper>
            </Box>

            <Dialog
                open={exportDialog.open}
                onClose={() => !startingExport && setExportDialog((prev) => ({ ...prev, open: false }))}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ fontSize: 18, fontWeight: 500 }}>Send data export</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontSize: 14, mb: 2 }}>
                        Your data is prepared as a secure, encrypted file. Everyone you add below gets
                        an email with a link to the file and the secret key that opens it. You can also
                        find both in the <b>Exports</b> tab.
                    </DialogContentText>

                    {/* A name is what makes this recognisable in Exports and
                        History later, and it goes in the email subject. */}
                    <TextField
                        label="Name this export"
                        placeholder="e.g. Course details query"
                        value={exportDialog.name}
                        onChange={(e) => setExportDialog((prev) => ({ ...prev, name: e.target.value }))}
                        inputProps={{ maxLength: 100 }}
                        helperText="Optional. Shown in the Exports and History tabs and in the email subject."
                        fullWidth
                        sx={{ mb: 2 }}
                    />

                    <Box
                        sx={{
                            p: 1.25, mb: 2, backgroundColor: "#f4f6f8", border: "1px solid #e0e0e0",
                            borderRadius: 1, fontFamily: MONO, fontSize: 12, maxHeight: 120, overflow: "auto",
                            whiteSpace: "pre-wrap", wordBreak: "break-word",
                        }}
                    >
                        {exportDialog.sql}
                    </Box>

                    <Autocomplete
                        multiple
                        freeSolo
                        options={[]}
                        value={exportDialog.emails}
                        inputValue={emailInput}
                        onChange={(event, value) => setExportDialog((prev) => ({
                            ...prev,
                            // A pasted line can carry several addresses at once.
                            emails: [...new Set(value.flatMap(splitAddresses))],
                        }))}
                        onInputChange={(event, value) => {
                            // Enter is Autocomplete's own; a comma or space should
                            // finish an address too, the way mail clients do.
                            if (/[,;\s]$/.test(value)) {
                                const entries = splitAddresses(value);
                                if (entries.length) {
                                    setExportDialog((prev) => ({
                                        ...prev,
                                        emails: [...new Set([...prev.emails, ...entries])],
                                    }));
                                }
                                setEmailInput("");
                                return;
                            }
                            setEmailInput(value);
                        }}
                        renderTags={(value, getTagProps) =>
                            value.map((email, index) => {
                                const { key, ...chipProps } = getTagProps({ index });
                                return (
                                    <Chip
                                        key={key}
                                        label={email}
                                        size="small"
                                        variant="outlined"
                                        // A mistyped address is obvious before sending.
                                        color={EMAIL_RE.test(email) ? "default" : "error"}
                                        {...chipProps}
                                    />
                                );
                            })
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Mail to"
                                placeholder={exportDialog.emails.length ? "" : "name@example.com"}
                                helperText="Press Enter or comma to add another address. Leave empty to skip the email and use the Exports tab."
                            />
                        )}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => {
                            setEmailInput("");
                            setExportDialog((prev) => ({ ...prev, open: false }));
                        }}
                        disabled={startingExport}
                        sx={{ textTransform: "none" }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={submitExport}
                        disabled={startingExport}
                        startIcon={startingExport ? <CircularProgress size={16} color="inherit" /> : <BsCloudUpload />}
                        sx={{ textTransform: "none" }}
                    >
                        {startingExport ? "Starting..." : "Start export"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default QueryTool;
